import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Divider from "@mui/material/Divider";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import { InlineIcon } from "@iconify/react";
import Tooltip from "@mui/material/Tooltip";
import { codeStyle } from "./TextPreview";
import { ButtonLink } from "./Image";
import { Instance } from "@popperjs/core";

import { useAtomValue, useSetAtom } from "jotai";

import * as slackemojis from "./slackemojis.json";

import { useState, useEffect, Fragment, useRef } from "react";
import { StoryPopup } from "./StoryCard";
import { StoryFastPreview } from "./StoryDetails";
import { TextPreview } from "./TextPreview";

import { activeLabelsState, searchState } from "./JotaiAtoms";

import {
  projectPickerState,
  peopleIndexState,
  AccountInfo,
} from "./JotaiAtoms";

import * as u from "./utility";
import * as riki from "jsriki";

import { useNewTabText } from "./hooks";
import { MessageItem, MdPiece } from "./MessageItem";
import { DEFAULT_FONT_SIZE, HIGHTLIGHT_COLOR } from "./constants";
import JSONPretty from "react-json-pretty";
import { FileUploadPreview, UnidiffRust } from "./FileUploadPreview";

const translateEmoji = (name: string) => {
  if (name.length < 3) {
    return null;
  }

  if (name[0] === ":") {
    name = name.slice(1, name.length - 1);
  }

  return (slackemojis as any)[name] || null;
};

function collectTextWithBreaks(node: HTMLElement): string {
  let buffer = "";

  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      buffer += child.textContent || "";
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = (child as HTMLElement).tagName.toLowerCase();
      console.log(tag);

      if (tag === "br") {
        buffer += "\n";
      } else {
        buffer += collectTextWithBreaks(child as HTMLElement);

        // Add line break after block-level tags
        if (
          [
            "p",
            "div",
            "section",
            "article",
            "header",
            "footer",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
          ].includes(tag)
        ) {
          buffer += "\n";
        }
      }
    }
  });

  return buffer;
}

type CodeLineProps = {
  fontSize?: number;
  st_pk?: string;
  st_sk?: string;
  children: any;
  sx?: any;
};
function CodeLine({ fontSize, st_pk, st_sk, sx, children }: CodeLineProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const popperRef = useRef<Instance>(null);

  const positionRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const handleCopy = () => {
    let text = "";
    if (ref.current) {
      text = collectTextWithBreaks(ref.current);
    }

    navigator.clipboard.writeText(text);
    setOpen(false);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    let rect = ref.current!.getBoundingClientRect();
    const clientRects = ref.current!.getClientRects();
    if (clientRects.length > 1) {
      rect = clientRects[clientRects.length - 1];
    }
    positionRef.current = { x: rect.x + rect.width, y: rect.y };
    if (popperRef.current != null) {
      popperRef.current.update();
    }
  };

  return (
    <Tooltip
      placement="bottom-end"
      leaveDelay={400}
      enterDelay={0}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      title={
        <Fragment>
          <InlineIcon
            icon="solar:copy-bold"
            color="grey"
            style={{ cursor: "pointer", fontSize: "18px", opacity: 0.5 }}
            onClick={handleCopy}
          />
        </Fragment>
      }
      componentsProps={{
        tooltip: {
          sx: { p: 0, backgroundColor: "transparent" },
        },
      }}
      slotProps={{
        popper: {
          popperRef,
          anchorEl: {
            getBoundingClientRect: () => {
              return new DOMRect(positionRef.current.x, positionRef.current.y);
            },
          },
        },
      }}
      PopperProps={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [18, -13],
            },
          },
        ],
      }}
    >
      <Typography
        component="span"
        fontSize={fontSize}
        st_pk={st_pk}
        st_sk={st_sk}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        sx={{
          background: "#d0d0d0",
          color: "#352f2d",
          p: 0.35,
          borderRadius: "3px",
          ...(sx ? sx : {}),
        }}
      >
        <>{children}</>
      </Typography>
    </Tooltip>
  );
}

function CodeBlockText({ text }: { text: string }) {
  const [displayText, setDisplayText, handleDoubleClick] = useNewTabText();
  useEffect(() => {
    setDisplayText(text);
  }, [text]);

  return <Box onClick={handleDoubleClick}>{displayText}</Box>;
}

type CodeBlockProps = {
  st_pk: string;
  st_sk: string;
  data: string;
  fontSize: number;
  index: number;
  item: MessageItem;
};

const render = (data: string) => {
  switch (u.determineTextMimeType(data)) {
    case "application/json":
      return <JSONPretty data={data} />;
    case "text/x-diff":
      return <UnidiffRust data={data} />;
    default:
      return <>{data}</>;
  }
};

function CodeBlock({
  st_pk,
  st_sk,
  data,
  fontSize,
  item,
  index,
}: CodeBlockProps) {
  return (
    <Box st_pk={st_pk} st_sk={st_sk}>
      <TextPreview
        data={data}
        fontSize={fontSize}
        item={item}
        fileName={"code.txt"}
        index={index}
        mimetype="text/plain"
        propPrefix="codeblock"
      >
        {render}
      </TextPreview>
    </Box>
  );
}

type CodeBlockOldProps = {
  st_pk: string;
  st_sk: string;
  children: any;
  fontSize: number;
};

function CodeBlockOld({ st_pk, st_sk, children, fontSize }: CodeBlockOldProps) {
  const [hover, setHover] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<HTMLElement>();

  return (
    <Box
      sx={codeStyle(fontSize)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setAnchor(undefined);
      }}
    >
      {hover && (
        <Box sx={{ position: "absolute", right: 0, zIndex: 5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              navigator.clipboard.writeText(children);
              setAnchor(e.currentTarget);
              setTimeout(() => {
                setAnchor(undefined);
              }, 1000);
            }}
          >
            <ContentCopyIcon fontSize="inherit" sx={{ color: "white" }} />
          </IconButton>
          <Popper
            open={anchor != null}
            anchorEl={anchor}
            placement="right-start"
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <Typography fontSize={fontSize * 0.8} sx={{ p: 1 }}>
                    copied!
                  </Typography>
                </Paper>
              </Fade>
            )}
          </Popper>
        </Box>
      )}

      <code st_pk={st_pk} st_sk={st_sk}>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: fontSize * 0.85,
          }}
        >
          <CodeBlockText text={children} />
        </pre>
      </code>
    </Box>
  );
}

function unescapeSlackEntities(str: string) {
  return str
    .replaceAll("&amp;", "&")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">");
}

function injectMarkDownIndecies(
  piece: MdPiece,
  start: [number, number, number]
): [number, number, number] {
  let index = start;
  if (piece.t === "cb" || piece.t === "cbx") {
    piece.codeBlockIndex = index[0];
    index[0] += 1;
  } else if (piece.t === "txt") {
    piece.txtBlockIndex = index[1];
    index[1] += 1;
  } else if (piece.t === "img" || piece.t === "fx") {
    piece.fileIndex = index[2];
    index[2] += 1;
  } else if (piece.t !== "t" && typeof piece.v === "object") {
    for (let subpiece of piece.v as MdPiece[]) {
      index = injectMarkDownIndecies(subpiece, index);
    }
  }

  return index;
}

function compactMarkdown(
  parsed: MdPiece[],
  unescape?: boolean,
  result?: MdPiece[],
  markdownIndeciesStart?: [number, number, number]
) {
  let compacted: MdPiece[] = result || [];
  let markdownIndecies = markdownIndeciesStart || [0, 0, 0];

  const pushRaw = (v: MdPiece) => {
    compacted.push(v);
  };

  const pushUnescaped = (v: MdPiece) => {
    if (typeof v.v === "string") {
      v.v = unescapeSlackEntities(v.v);
    }
    pushRaw(v);
  };

  const push = unescape ? pushUnescaped : pushRaw;

  for (let i = 0; i < parsed.length; ++i) {
    const cur = parsed[i];
    if (cur.t == "t") {
      // tables are weird, do nothing
      push(cur);
    } else if (typeof cur.v === "object") {
      let nested: MdPiece[] = [];
      compacted.push({ t: cur.t, v: nested });
      compactMarkdown(cur.v as MdPiece[], unescape, nested, markdownIndecies);
    } else if (i === 0) {
      push(parsed[i]);
      markdownIndecies = injectMarkDownIndecies(cur, markdownIndecies);
    } else {
      const prev = compacted[compacted.length - 1];
      if (prev.t === "br" && cur.t === "br") {
        compacted[compacted.length - 1] = { t: "br2" };
      } else {
        markdownIndecies = injectMarkDownIndecies(cur, markdownIndecies);
        push(cur);
      }
    }
  }

  return compacted;
}

export function preparseTextWithMath(
  text: string,
  math: { start: number; end: number }[]
): MdPiece[] {
  let flatten: number[] = [];
  for (const m of math) {
    flatten.push(m.start);
    flatten.push(m.end);
  }
  let parsed = JSON.parse(riki.parse_to_json(text, flatten));
  return compactMarkdown(parsed, true);
}

export async function parseMarkdown(
  text: string,
  item: MessageItem
): Promise<MdPiece[]> {
  // const test = [{t: 'h1', v : [{t: 'txt', v: 'hell world'}]}, {t: 'br'}, {t: 'br'}, {t: 'txt', v: 'hello world'}];
  // return compactMarkdown(test);

  // const test = [
  //     { t: "txt", v: "hell world" },
  //     { t: "hr" },
  //     { t: "txt", v: "hello world" },
  //   ];
  //   return compactMarkdown(test);

  // const test = [{t: "txt", v: "value"}, {t: "cx", v: "world", s: "~"}, {t: "txt", v: "world", s: "~"}];
  // return compactMarkdown(test);

  // const test = [{t: "txt", v: "AAA"}, {t: 'sub', v : [{t: 'txt', v: 'hell world'}, {t: "cx", v: "codeline"}]}, {t: 'br'}, {t: 'br'}, {t: 'txt', v: 'hello world'}];
  // return compactMarkdown(test);

  // const test = [{t: "u", v: "@ggerganov"}, {t: "br"}, {t: "u", v: "@acbits"}];
  // return compactMarkdown(test);

  // const test = [{t: "txt", v: "AAA"}, {t: 'ins', v : [{t: 'txt', v: 'hell world'}, {t: "cx", v: "codeline"}]}, {t: 'br'}, {t: 'br'}, {t: 'txt', v: 'hello world'}];
  // return compactMarkdown(test);

  // const test = [{t: "txt", v: "AAA"}, {t: 'mark', v : [{t: 'txt', v: 'hell world'}, {t: "cx", v: "codeline"}]}, {t: 'br'}, {t: 'br'}, {t: 'txt', v: 'hello world'}];
  // return compactMarkdown(test);

  // const test = [
  //   { t: "txt", v: "AAA" },
  //   {
  //     t: "code",
  //     v: [
  //       { t: "txt", v: "hell world" },
  //       { t: "br" },
  //       { t: "br" },
  //       { t: "txt", v: "hello world", s: "~" },
  //       { t: "br" },
  //       { t: "txt", v: "hello world" },
  //     ],
  //   },
  // ];
  // return compactMarkdown(test);

  // const test = JSON.parse('[{"t":"table","v":[{"t":"tr","v":[{"t":"td","v":[{"t":"txt","v":"hello"}]},{"t":"td","v":[{"t":"txt","v":"world"}]}]}]}]');
  // return test;

  // const test = [{t:"a:https://foobar.com",v:[{t: "cx", v: "Google"}, {t: "br"}, {t: "txt", v: "Search"}]}];
  // return test;

  if ("st_pp" in item) {
    if (typeof item.st_pp === "string") {
      return compactMarkdown(await u.parseJsonFromBinary(item.st_pp));
    } else if (item.st_pp) {
      return compactMarkdown(item.st_pp);
    }
  }
  return preparseTextWithMath(text, []);
}

const getStableKey = (chunk: MdPiece) => {
  if (chunk.t === "cbx" || chunk.t === "cb") {
    return `codeblock-${chunk.codeBlockIndex}`;
  } else if (chunk.t === "img" || chunk.t === "fx") {
    return `file-${chunk.fileIndex}`;
  }
  return undefined;
};

type MarkdownProps = {
  parsed: MdPiece[];
  item: MessageItem;
  fontSize?: number;
  start: number;
  includeNodeIds?: boolean;
  sx?: any;
};
export function Markdown({
  parsed,
  item,
  fontSize,
  start,
  sx,
  includeNodeIds,
}: MarkdownProps) {
  return (
    <>
      {parsed.map((chunk, i) => (
        <MarkdownChunk
          chunk={chunk}
          type={chunk.t}
          value={chunk.v}
          refs={chunk.r}
          textStyle={chunk.s}
          customStyle={chunk.css}
          item={item}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
          index={start + i}
          end={start + parsed.length}
          sx={sx}
          includeNodeIds={includeNodeIds}
          key={getStableKey(chunk) || start + i}
        />
      ))}
    </>
  );
}

export function QuoteBar({ children }: { children: any }) {
  return (
    <Box
      sx={{
        borderLeft: 8,
        borderLeftColor: "dimgrey",
        pl: 1,
        ml: 0.4,
        mt: 0.5,
      }}
    >
      {children}
    </Box>
  );
}

type MarkdownChunkProps = {
  chunk: MdPiece;
  type: string;
  value?: string | MdPiece[] | MdPiece[][][];
  item: MessageItem;
  refs?: string[];
  textStyle?: string;
  fontSize: number;
  index: number;
  end: number;
  sx?: any;
  customStyle?: { [key: string]: any };
  includeNodeIds?: boolean;
};

function MarkdownChunk({
  chunk,
  type,
  value,
  item,
  refs,
  textStyle,
  fontSize,
  index,
  end,
  sx,
  customStyle,
  includeNodeIds,
}: MarkdownChunkProps) {
  const projectName = useAtomValue(projectPickerState);
  const refStory = { st_pk: item.PK, st_sk: item.SK };
  const setSearch = useSetAtom(searchState);

  const getCustomStyle = () => {
    if (!customStyle) {
      return {};
    }

    let out: any = {};
    for (const [k, v] of Object.entries(customStyle)) {
      if (k === "font-weight") {
        out.fontWeight = v;
      } else if (k === "font-size") {
        out.fontSize = v;
      } else if (k == "color") {
        out[k] = v;
      }
    }
    return out;
  };

  const urlFromStoryKey = (key: string) => {
    return (
      window.location.origin +
      `/story/${projectName}/${key.split("-")[1]}${window.location.search}`
    );
  };

  const urlFromRef = (ref: string) => {
    const key = ref.slice(1, -1);
    let url =
      window.location.origin +
      `/story/${projectName}/${encodeURIComponent(key)}${
        window.location.search
      }`;

    const timeref = item.st_created_at || item.ts || item.event_ts;
    if (timeref) {
      url += `/${encodeURIComponent(timeref)}`;
    }

    return url;
  };

  const urlFromLink = (link: string, trim?: number) => {
    const trimmed = trim ? link.slice(trim, -trim) : link;
    return trimmed.split("|")[0];
  };

  const annotationFromLink = (link: string, trim?: number) => {
    const v = trim ? link.slice(trim, -trim) : link;
    const delim = v.indexOf("|");
    if (delim === -1) {
      return v;
    } else {
      const annotation = v.slice(delim + 1);
      return annotation.length ? annotation : v.slice(0, delim);
    }
  };

  const fileNameFromLinkWithAnnotation = (link: string) => {
    const v = link;
    const delim = v.indexOf("|");
    if (delim === -1) {
      const url = new URL(v);
      const segments = url.pathname.split("/").filter(Boolean);
      const lastSegment = segments.at(-1);
      return lastSegment;
    } else {
      const annotation = v.slice(delim + 1);
      return annotation.length ? annotation : v.slice(0, delim);
    }
  };

  const getCode = (value: string | MdPiece[], wrap: number) => {
    if (typeof value === "string") {
      return value.slice(wrap, -wrap);
    } else {
      let res = "";
      for (let p of value) {
        if (p.t === "txt") {
          res += p.v;
        } else if (p.t === "lnk") {
          res += p.v!.slice(1, -1);
        } else if (p.t == "br") {
          res += "\n";
        }
      }
      return res;
    }
  };

  const convertTextStyle = (textStyle?: string) => {
    let sx: any = {};
    if (textStyle) {
      let done = false;
      if (textStyle.includes("_")) {
        sx.fontStyle = "italic";
        done = true;
      }
      if (textStyle.includes("*")) {
        sx.fontWeight = "bold";
        done = true;
      }
      if (textStyle.includes("~")) {
        sx.textDecoration = "line-through";
        done = true;
      }
      if (textStyle.includes("!")) {
        sx.backgroundColor = HIGHTLIGHT_COLOR;
        done = true;
      }

      if (!done) {
        // previous format
        if (textStyle.includes("i")) {
          sx.fontStyle = "italic";
        }
        if (textStyle.includes("b")) {
          sx.fontWeight = "bold";
        }
        if (textStyle.includes("s")) {
          sx.textDecoration = "line-through";
        }
      }
    }

    return sx;
  };

  if (type === "txt") {
    // text
    return (
      <Typography
        {...refStory}
        component="span"
        fontSize={fontSize}
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          ...getCustomStyle(),
        }}
        st_md={
          true || includeNodeIds
            ? `${chunk.txtBlockIndex}:${chunk.txtBlockOffset || 0}`
            : undefined
        }
        sx={convertTextStyle(textStyle)}
      >
        {value as string}
      </Typography>
    );
  } else if (type === "stk") {
    // strikethrough -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ textDecoration: "line-through" }}
      >
        {(value as string).slice(1, -1)}
      </Typography>
    );
  } else if (type === "it") {
    // italic -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ fontStyle: "italic" }}
      >
        {(value as string).slice(1, -1)}
      </Typography>
    );
  } else if (type === "bld") {
    // bold -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ fontWeight: "bold" }}
      >
        {(value as string).slice(1, -1)}
      </Typography>
    );
  } else if (type === "url") {
    // raw url
    return (
      <>
        <Link href={value as string} fontSize={fontSize} sx={sx}>
          {value as string}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <StoryReference
              url={urlFromRef(`[${refs[0]}]`)}
              storyId={`[${refs[0]}]`}
              sx={sx}
            />
          </sup>
        )}
      </>
    );
  } else if (type === "br") {
    // line break
    return <br />;
  } else if (type === "hr") {
    return (
      <>
        <div
          style={{
            height: "3px",
            width: "50%",
            backgroundColor: "#eeeeee",
            marginTop: 5,
            marginBottom: 5,
          }}
        />
      </>
    );
  } else if (type === "br2") {
    // a pair of line breaks
    return (
      <>
        <br />
        <span style={{ display: "block", height: "0.3em", width: "0px" }} />
      </>
    );
  } else if (type === "rk") {
    // reference key like STORY-123
    return (
      <StoryReference
        storyId={value as string}
        url={urlFromStoryKey(value as string)}
        fontSize={fontSize}
        sx={sx}
      />
    );
  } else if (type === "ref") {
    // reference enclosed in square bracket like [123]
    return (
      <StoryReference
        storyId={value as string}
        url={urlFromRef(value as string)}
        fontSize={fontSize}
        sx={sx}
      />
    );
  } else if (type === "lnk") {
    // link enclosed in angle brackets <...>
    return (
      <>
        <Link
          href={urlFromLink(value as string, 1)}
          sx={{
            ...convertTextStyle(textStyle),
            fontSize: fontSize,
            ...(sx || {}),
          }}
        >
          {annotationFromLink(value as string, 1)}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <StoryReference
              url={urlFromRef(`[${refs[0]}]`)}
              storyId={`[${refs[0]}]`}
              sx={sx}
            />
          </sup>
        )}
      </>
    );
  } else if (type === "fr") {
    // github's footnote reference
    return (
      <sup style={{ fontSize: fontSize * 0.62 }}>
        <Link href={`#${item.PK}-${item.SK}-footnote-${value}`} sx={sx}>
          [{value as string}]
        </Link>
      </sup>
    );
  } else if (type === "fd") {
    // github's footnote definition
    return (
      <>
        {value === "1" && <Divider />}
        <span id={`${item.PK}-${item.SK}-footnote-${value}`}>
          {value + ". "}
        </span>
      </>
    );
  } else if (type === "footnote") {
    // github's footnote
    return (
      <>
        <br />
        <br />
        <Markdown
          parsed={value as any[]}
          item={item}
          fontSize={fontSize}
          start={end}
          sx={sx}
        />
      </>
    );
  } else if (type === "lx") {
    // github's link
    return (
      <>
        <Link
          href={urlFromLink(value as string, 0)}
          sx={{ ...convertTextStyle(textStyle), ...(sx || {}) }}
          fontSize={fontSize}
          style={getCustomStyle()}
        >
          {annotationFromLink(value as string, 0)}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <StoryReference
              url={urlFromRef(`[${refs[0]}]`)}
              storyId={`[${refs[0]}]`}
              sx={sx}
            />
          </sup>
        )}
      </>
    );
  } else if (type === "fx") {
    // github's file attachment
    return (
      <FileUploadPreview
        files={[
          {
            name: fileNameFromLinkWithAnnotation(value as string),
            uri: urlFromLink(value as string, 0),
          },
        ]}
        item={item}
        markdown
      />
    );
  } else if (type === "u") {
    // slack's username: <@U04QPK9JM28>
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
        sx={{
          backgroundColor: "#e0f0f9ff",
          borderRadius: "4px",
          p: 0.3,
          pl: 0.5,
          pr: 0.5,
        }}
      >
        <UserName
          user={{ app: item.st_source, userId: value as string }}
          prefixName="@"
          onClick={(accounts: AccountInfo[]) => {
            setSearch([accounts]);
          }}
        />
      </Typography>
    );
  } else if (type === "c") {
    // slacks' channel name
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
      >
        #<ChannelName channelId={(value as string).slice(2, -1)} />
      </Typography>
    );
  } else if (type === "e") {
    // emoji like :smile:
    return (
      <Typography
        component="span"
        fontSize={translateEmoji(value as string) ? fontSize * 1.25 : fontSize}
        style={{ whiteSpace: "pre-wrap", lineHeight: "0em" }}
      >
        {translateEmoji(value as string) || (value as string)}
      </Typography>
    );
  } else if (type === "b") {
    // slack's broadcast like @here
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
      >
        @{(value as string).slice(2, -1).split("|").slice(-1)}
      </Typography>
    );
  } else if (type === "s") {
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
      >
        @{(value as string).slice(0, -1).split("^").slice(-1)}
      </Typography>
    );
  } else if (type === "cl") {
    // codeline `code`
    return (
      <CodeLine {...refStory} fontSize={fontSize}>
        {getCode(value as string | MdPiece[], 1)}
      </CodeLine>
    );
  } else if (type === "cx") {
    // github's codeline
    return (
      <CodeLine
        {...refStory}
        fontSize={fontSize}
        sx={convertTextStyle(textStyle)}
      >
        {value}
      </CodeLine>
    );
  } else if (type === "cb") {
    // codeblock ```code block```
    // or unterminated code block ```code block
    let code = "";
    if (typeof value === "string") {
      code = value.slice(3);
      if (code.endsWith("```")) {
        code = code.slice(0, code.length - 3);
      }
    } else {
      code = getCode(value as string | MdPiece[], 3);
    }
    return (
      <CodeBlock
        {...refStory}
        fontSize={fontSize}
        item={item}
        index={chunk.codeBlockIndex || 0}
        data={u.trimNewLine(code)}
      />
    );
  } else if (type === "cbx") {
    //github's code block
    return (
      <CodeBlock
        {...refStory}
        fontSize={fontSize}
        item={item}
        index={chunk.codeBlockIndex || 0}
        data={u.trimNewLine(value as string)}
      />
    );
  } else if (type === "q") {
    // quote
    return (
      <QuoteBar>
        <Markdown
          parsed={value as any[]}
          item={item}
          fontSize={fontSize}
          start={end}
          sx={sx}
        />
      </QuoteBar>
    );
  } else if (type === "t") {
    // table
    return (
      <TableContainer>
        <Table size="small" sx={{ width: "auto" }}>
          <TableHead>
            <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
              {(value as any[])[0].map((cell: any, i: number) => (
                <TableCell key={i}>
                  <Markdown
                    parsed={cell}
                    item={item}
                    fontSize={fontSize}
                    start={end}
                    sx={sx}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(value as any[]).slice(1).map((row, i) => (
              <TableRow key={i} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                {row.map((cell: any, j: number) => (
                  <TableCell key={j}>
                    <Markdown
                      parsed={cell}
                      item={item}
                      fontSize={fontSize}
                      start={end}
                      sx={sx}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else if (type === "h") {
    return (
      <>
        <Typography
          component="span"
          fontSize={fontSize}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {value as string}
          {refs && refs.length && (
            <sup style={{ fontSize: fontSize * 0.62 }}>
              <StoryReference
                url={urlFromRef(`[${refs[0]}]`)}
                storyId={`[${refs[0]}]`}
                sx={sx}
              />
            </sup>
          )}
        </Typography>
      </>
    );
  } else if (type === "img") {
    // github image
    return (
      <FileUploadPreview
        files={[
          {
            type: "image/unknown",
            name: fileNameFromLinkWithAnnotation(value as string),
            uri: urlFromLink(value as string, 0),
          },
        ]}
        item={item}
        markdown
      />
    );
  } else if (type[0] === "h" && type[1] >= "1" && type[1] <= "6") {
    // header
    return (
      <Markdown
        parsed={value as any[]}
        item={item}
        fontSize={fontSize * 1.2}
        start={end}
        sx={sx}
      />
    );
  } else if (type === "sup") {
    return (
      <sup>
        <Markdown
          parsed={value as any[]}
          item={item}
          fontSize={fontSize * 0.8}
          start={end}
          sx={sx}
        />
      </sup>
    );
  } else if (type === "sub") {
    return (
      <sub>
        <Markdown parsed={value as any[]} item={item} start={end} sx={sx} />
      </sub>
    );
  } else if (type === "mark") {
    return (
      <mark>
        <Markdown parsed={value as any[]} item={item} start={end} sx={sx} />
      </mark>
    );
  } else if (type === "ins") {
    return (
      <ins>
        <Markdown parsed={value as MdPiece[]} item={item} start={end} sx={sx} />
      </ins>
    );
  } else if (type === "code") {
    return (
      <CodeLine
        {...refStory}
        fontSize={fontSize}
        sx={convertTextStyle(textStyle)}
      >
        <Markdown parsed={value as MdPiece[]} item={item} start={end} sx={sx} />
      </CodeLine>
    );
  } else if (type === "table") {
    // table
    // example {t: "table", v: [t: "tr", v: [{t: "td", v: [{"txt", "hello"}]}]]}
    return (
      <TableContainer>
        <Table size="small" sx={{ width: "auto" }}>
          <TableBody>
            {value &&
              (value as MdPiece[]).map((row, i) => (
                <TableRow key={i} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  {row.t === "tr" &&
                    row.v &&
                    (row.v as MdPiece[]).map((cell: any, j: number) => (
                      <TableCell key={j}>
                        <Markdown
                          parsed={cell.v}
                          item={item}
                          fontSize={fontSize}
                          start={end}
                          sx={sx}
                        />
                      </TableCell>
                    ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else if (type.startsWith("a:")) {
    return (
      <Link href={type.slice(2)} target="_blank" rel="noreferrer">
        <Markdown
          parsed={value as MdPiece[]}
          item={item}
          start={end}
          sx={sx}
          fontSize={fontSize}
        />
      </Link>
    );
  } else {
    return <div />;
  }
}

type StoryReferenceProps = {
  storyId: string;
  url: string;
  fontSize?: number;
  sx: any;
};
function StoryReference({ storyId, url, fontSize, sx }: StoryReferenceProps) {
  return (
    <StoryPopup
      storyMoniker={storyId}
      enterDelay={300}
      previewComponent={StoryFastPreview}
    >
      <Link href={url} sx={{ fontSize: fontSize, ...(sx || {}) }}>
        {storyId}
      </Link>
    </StoryPopup>
  );
}

const getUserId = (userId: string, sourceApp?: string) => {
  if (
    sourceApp === "slack" ||
    (!sourceApp && userId.length > 0 && userId[0] == "<")
  ) {
    return userId.slice(2, -1);
  }

  if (userId.length > 0 && userId[0] === "@") {
    return userId.slice(1);
  }

  return userId;
};

export function UserName({
  user,
  onClick,
  fontSize,
  prefixName,
}: {
  user: { userId: string; app?: string };
  onClick?: (accounts: AccountInfo[]) => void;
  fontSize?: number;
  prefixName?: string;
}) {
  const data = useAtomValue(peopleIndexState);
  const [displayUser, setDisplayUser] = useState<string>(
    getUserId(user.userId)
  );

  useEffect(() => {
    if (data) {
      setDisplayUser((prev: string) => {
        const userId = getUserId(user.userId);
        const user_key = `${user.app}:${userId}`;
        const found = data.get(user_key);
        if (found) {
          let name = "";
          for (const acc of found) {
            if (acc.name && acc.name.length > name.length) {
              name = acc.name;
            }
          }
          if (name) {
            return name;
          }
          for (const acc of found) {
            if (acc.email) {
              return acc.email;
            }
          }

          for (const acc of found) {
            if (acc.username) {
              return acc.username;
            }
          }
        }

        return userId;
      });
    }
  }, [data, user]);

  const handleClick = () => {
    if (!data || !onClick) {
      return;
    }
    const userId = getUserId(user.userId);
    const key = `${user.app}:${userId}`;
    const found = data.get(key);
    if (!found) {
      return;
    }
    onClick(found);
  };

  if (!onClick) {
    return <>{(prefixName || "") + displayUser}</>;
  } else {
    return (
      <ButtonLink
        text={(prefixName || "") + displayUser}
        onclick={handleClick}
        fontSize={fontSize}
      ></ButtonLink>
    );
  }
}

function ChannelName({ channelId }: { channelId: string }) {
  const data = useAtomValue(activeLabelsState);
  const [channel, setChannel] = useState<string>(
    channelId.split("|").slice(-1)[0]
  );

  useEffect(() => {
    if (data) {
      setChannel((prev: string) => {
        if (prev && data.prev) {
          const display = data[channel].display;
          let out = data[channel].name;
          if (typeof display === "string" && display) {
            out = display;
          }
          if (out && out.startsWith("#")) {
            return out.slice(1);
          }
          if (out) {
            return out;
          }
        }
        return prev;
      });
    }
  }, [data]);

  return <>{channel}</>;
}
